import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import ExportButton from "../../components/ExportButton";
import PeriodFilter from "../../components/PeriodFilter";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Send, MapPin, BarChart3 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

const TASK_CATEGORIES = ["Fieldwork","Review","Planning","Client Meeting","Documentation","Filing","Research","Travel","Admin","Other"];
const todayIso = () => new Date().toISOString().slice(0,10);
const EMPTY = { project_id: "", task_category: "Fieldwork", description: "", task_date: todayIso(), hours: 8, billable: true, work_location: "Office", start_time: "", end_time: "", geo_lat: null, geo_lng: null, geo_accuracy: null, manual_location: "" };

export default function Timesheet() {
  const { user } = useAuth();
  const isMgmt = ["partner","admin","manager"].includes(user?.role);
  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState("mine");
  const [userFilter, setUserFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [period, setPeriod] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [geoStatus, setGeoStatus] = useState(""); // 'capturing' | 'ok' | 'denied' | 'unavailable'
  const [compOffOpen, setCompOffOpen] = useState(false);
  const [compOffForm, setCompOffForm] = useState({ start_date: todayIso(), end_date: todayIso(), days: 1, reason: "" });
  const [view, setView] = useState("entries");
  const [groupBy, setGroupBy] = useState("user");
  const [summary, setSummary] = useState(null);

  const captureGeo = () => {
    if (!navigator.geolocation) { setGeoStatus("unavailable"); return; }
    setGeoStatus("capturing");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, geo_lat: pos.coords.latitude, geo_lng: pos.coords.longitude, geo_accuracy: pos.coords.accuracy }));
        setGeoStatus("ok");
        toast.success(`Location captured (±${Math.round(pos.coords.accuracy)}m)`);
      },
      (err) => { setGeoStatus("denied"); toast.error("Location denied. Enter manually below."); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const submitCompOff = async (e) => {
    e?.preventDefault();
    if (!compOffForm.reason.trim()) { toast.error("Reason required"); return; }
    try {
      await api.post("/comp-off/claims", { work_date: compOffForm.start_date, hours_worked: Number(compOffForm.days) * 8, details: compOffForm.reason });
      toast.success("Comp-Off claim submitted");
      setCompOffOpen(false);
      setCompOffForm({ start_date: todayIso(), end_date: todayIso(), days: 1, reason: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const dateParams = `${startDate ? `&start_date=${startDate}` : ""}${endDate ? `&end_date=${endDate}` : ""}`;

  const load = () => Promise.all([
    api.get(`/timesheets?scope=${scope}${userFilter ? `&user_id=${userFilter}` : ""}${clientFilter ? `&client_id=${clientFilter}` : ""}${dateParams}`).then((r) => setList(r.data)),
    api.get("/projects").then((r) => setProjects(r.data)),
    isMgmt ? api.get("/users").then((r) => setUsers(r.data)) : Promise.resolve(),
    isMgmt ? api.get("/clients").then((r) => setClients(r.data)) : Promise.resolve(),
  ]);
  useEffect(() => { load(); }, [scope, userFilter, clientFilter, startDate, endDate]);

  const loadSummary = () => {
    const params = [`group_by=${groupBy}`];
    if (userFilter) params.push(`user_id=${userFilter}`);
    if (clientFilter) params.push(`client_id=${clientFilter}`);
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    api.get(`/timesheets/summary?${params.join("&")}`).then((r) => setSummary(r.data)).catch(() => setSummary(null));
  };
  useEffect(() => { if (view === "summary") loadSummary(); }, [view, groupBy, userFilter, clientFilter, startDate, endDate]);

  const openCreate = () => { setForm(EMPTY); setEditId(null); setGeoStatus(""); setOpen(true); };
  const openEdit = (t) => { setForm({ ...EMPTY, ...t }); setEditId(t.id); setGeoStatus(t.geo_lat ? "ok" : ""); setOpen(true); };

  const submit = async (e, asDraft = false) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, hours: Number(form.hours), save_as_draft: asDraft };
      if (editId) {
        await api.patch(`/timesheets/${editId}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/timesheets", payload);
        toast.success(asDraft ? "Saved as draft" : "Submitted");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submitDraft = async (id) => {
    try { await api.post(`/timesheets/${id}/submit`); toast.success("Submitted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete entry?")) return;
    try { await api.delete(`/timesheets/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const statusColor = (s) => ({
    draft: "border-slate-200 text-slate-700 bg-slate-50",
    submitted: "border-amber-200 text-amber-700 bg-amber-50",
    approved: "border-emerald-200 text-emerald-700 bg-emerald-50",
    rejected: "border-red-200 text-red-700 bg-red-50",
  }[s] || "border-slate-200");

  const totalHours = list.reduce((s, t) => s + (t.hours || 0), 0);
  const exportPath = `/exports/timesheets?scope=${scope}${userFilter ? `&user_id=${userFilter}` : ""}${clientFilter ? `&client_id=${clientFilter}` : ""}${dateParams}`;

  return (
    <div className="space-y-6 fade-in" data-testid="timesheet-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Timesheet</div>
          <h1 className="font-sans text-4xl mt-2">Hours & effort</h1>
          <p className="text-slate-600 text-sm mt-1">Showing {list.length} entries · <span className="serif-num text-xl text-slate-900">{totalHours.toFixed(1)}h</span> total</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <PeriodFilter
            value={period} start={startDate} end={endDate}
            onChange={({ period: p, start_date, end_date }) => { setPeriod(p); setStartDate(start_date || ""); setEndDate(end_date || ""); }}
            testId="ts-period"
          />
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="rounded-sm w-44" data-testid="ts-scope-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="mine">My entries</SelectItem>
              {isMgmt && <SelectItem value="all">All entries</SelectItem>}
              {isMgmt && <SelectItem value="pending">Pending only</SelectItem>}
              {isMgmt && <SelectItem value="history">History (approved/rejected)</SelectItem>}
            </SelectContent>
          </Select>
          {isMgmt && (scope === "all" || scope === "history" || scope === "pending") && (
            <Select value={userFilter || "_all"} onValueChange={(v)=>setUserFilter(v === "_all" ? "" : v)}>
              <SelectTrigger className="rounded-sm w-44" data-testid="ts-user-filter"><SelectValue placeholder="Filter by user"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All users</SelectItem>
                {users.map((u)=><SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {isMgmt && (scope === "all" || scope === "history" || scope === "pending") && (
            <Select value={clientFilter || "_all"} onValueChange={(v)=>setClientFilter(v === "_all" ? "" : v)}>
              <SelectTrigger className="rounded-sm w-44" data-testid="ts-client-filter"><SelectValue placeholder="Filter by client"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All clients</SelectItem>
                {clients.map((c)=><SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <ExportButton path={exportPath} filename="timesheets.csv" testId="ts-export"/>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-timesheet-btn"><Plus className="h-4 w-4 mr-2"/>Log Hours</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-sans text-2xl">{editId ? "Edit entry" : "Log hours"}</DialogTitle></DialogHeader>
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900 mb-2" data-testid="ts-holiday-note">
                Please obtain prior Partner approval for working on public holidays. Submit a Comp-Off request from the Comp-Off tab.
              </div>
              <form onSubmit={(e)=>submit(e, false)} className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label className="eyebrow">Project</Label>
                  <Select value={form.project_id} onValueChange={(v)=>setForm({...form, project_id: v})}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="ts-project-select"><SelectValue placeholder="Select project"/></SelectTrigger>
                    <SelectContent>{projects.map((p)=><SelectItem key={p.id} value={p.id}>{p.client_name} · {p.project_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="eyebrow">Date</Label><Input type="date" required value={form.task_date} onChange={(e)=>setForm({...form, task_date: e.target.value})} className="mt-2 rounded-sm" data-testid="ts-date"/></div>
                <div><Label className="eyebrow">Hours</Label><Input type="number" step="0.25" min="0.25" max="24" required value={form.hours} onChange={(e)=>setForm({...form, hours: e.target.value})} className="mt-2 rounded-sm" data-testid="ts-hours"/></div>
                <div><Label className="eyebrow">Time In (HH:MM)</Label><Input type="time" value={form.start_time || ""} onChange={(e)=>{ const st = e.target.value; let h = form.hours; if (st && form.end_time) { const [sh,sm]=st.split(":").map(Number); const [eh,em]=form.end_time.split(":").map(Number); const diff = (eh*60+em - (sh*60+sm))/60; if (diff>0) h = diff.toFixed(2); } setForm({...form, start_time: st, hours: h}); }} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">Time Out (HH:MM)</Label><Input type="time" value={form.end_time || ""} onChange={(e)=>{ const et = e.target.value; let h = form.hours; if (form.start_time && et) { const [sh,sm]=form.start_time.split(":").map(Number); const [eh,em]=et.split(":").map(Number); const diff = (eh*60+em - (sh*60+sm))/60; if (diff>0) h = diff.toFixed(2); } setForm({...form, end_time: et, hours: h}); }} className="mt-2 rounded-sm"/></div>
                <div><Label className="eyebrow">Task Category</Label>
                  <Select value={form.task_category} onValueChange={(v)=>setForm({...form, task_category: v})}>
                    <SelectTrigger className="mt-2 rounded-sm"><SelectValue/></SelectTrigger>
                    <SelectContent>{TASK_CATEGORIES.map((t)=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="eyebrow">Work Location</Label>
                  <Select value={form.work_location} onValueChange={(v)=>setForm({...form, work_location: v})}>
                    <SelectTrigger className="mt-2 rounded-sm"><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="Office">Office</SelectItem><SelectItem value="Client Place">Client Place</SelectItem><SelectItem value="Remote">Remote</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label className="eyebrow">Description</Label><Textarea rows={3} value={form.description || ""} onChange={(e)=>setForm({...form, description: e.target.value})} className="mt-2 rounded-sm" data-testid="ts-description"/></div>
                <div className="col-span-2">
                  <Label className="eyebrow">Type</Label>
                  <Select value={form.billable ? "billable" : "internal"} onValueChange={(v)=>setForm({...form, billable: v === "billable"})}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="ts-type-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="billable">Billable</SelectItem>
                      <SelectItem value="internal">Internal / Non-Chargeable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 border border-slate-200 rounded-sm p-4 bg-slate-50/50 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <Label className="eyebrow flex items-center gap-2"><MapPin className="h-3.5 w-3.5"/>Location (geo-tag)</Label>
                    <Button type="button" size="sm" variant="outline" onClick={captureGeo} className="rounded-sm" data-testid="ts-geo-btn">
                      {geoStatus === "capturing" ? "Capturing..." : geoStatus === "ok" ? "Re-capture" : "Capture Location"}
                    </Button>
                  </div>
                  {form.geo_lat && form.geo_lng && (
                    <div className="text-xs text-slate-600 font-mono" data-testid="ts-geo-display">
                      📍 {Number(form.geo_lat).toFixed(5)}, {Number(form.geo_lng).toFixed(5)} {form.geo_accuracy ? `(±${Math.round(form.geo_accuracy)}m)` : ""}
                    </div>
                  )}
                  {geoStatus === "denied" || geoStatus === "unavailable" ? (
                    <div className="text-xs text-amber-700">Geo-location {geoStatus}. Please enter location manually.</div>
                  ) : null}
                  <Input
                    placeholder="Manual location (used if geo-tagging unavailable)"
                    value={form.manual_location || ""}
                    onChange={(e)=>setForm({...form, manual_location: e.target.value})}
                    className="rounded-sm"
                    data-testid="ts-manual-location"
                  />
                </div>
                <DialogFooter className="col-span-2 gap-2">
                  {!editId && <Button type="button" onClick={(e)=>submit(e, true)} disabled={busy} variant="outline" className="rounded-sm border-slate-300" data-testid="ts-draft-btn">Save Draft</Button>}
                  <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="ts-save-btn">{busy ? "Saving..." : (editId ? "Update" : "Submit")}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList>
          <TabsTrigger value="entries" data-testid="ts-tab-entries">Entries</TabsTrigger>
          <TabsTrigger value="summary" data-testid="ts-tab-summary"><BarChart3 className="h-3.5 w-3.5 mr-1"/>Summary</TabsTrigger>
        </TabsList>
        <TabsContent value="entries" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
            <tr>
              <th className="text-left p-4">Date</th>
              {isMgmt && <th className="text-left p-4">User</th>}
              <th className="text-left p-4">Client</th>
              <th className="text-left p-4">Project</th>
              <th className="text-left p-4">Task</th>
              <th className="text-left p-4">In/Out</th>
              <th className="text-right p-4">Hours</th>
              <th className="text-left p-4">Type</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Remarks</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="timesheet-table">
            {list.length === 0 && <tr><td colSpan={isMgmt ? 11 : 10} className="p-8 text-center text-slate-500">No entries.</td></tr>}
            {list.map((t) => (
              <tr key={t.id} className="hover:bg-slate-50">
                <td className="p-4 font-mono text-xs">{t.task_date}</td>
                {isMgmt && <td className="p-4 text-slate-700">{t.user_name}</td>}
                <td className="p-4 text-slate-700">{t.client_name || "—"}</td>
                <td className="p-4 font-medium">{t.project_name}</td>
                <td className="p-4 text-slate-600">{t.task_category}</td>
                <td className="p-4 text-slate-500 text-xs font-mono">{t.start_time || "--"} / {t.end_time || "--"}</td>
                <td className="p-4 text-right serif-num">{t.hours}</td>
                <td className="p-4"><span className={`text-xs uppercase tracking-[0.2em] px-2 py-1 border rounded-full ${t.billable ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-slate-200 text-slate-700 bg-slate-50"}`}>{t.billable ? "Bill" : "Internal"}</span></td>
                <td className="p-4"><span className={`text-xs uppercase tracking-[0.2em] px-2 py-1 border rounded-full ${statusColor(t.approval_status)}`}>{t.approval_status}</span></td>
                <td className="p-4 text-xs text-slate-600 max-w-xs truncate" title={t.approval_remarks || ""}>{t.approval_remarks || (["approved","rejected"].includes(t.approval_status) ? "—" : "")}</td>
                <td className="p-4 text-right space-x-2 whitespace-nowrap">
                  {t.user_id === user.id && ["draft","rejected","submitted"].includes(t.approval_status) && <button onClick={()=>openEdit(t)} className="text-slate-400 hover:text-slate-900" title="Edit"><Pencil className="h-4 w-4 inline"/></button>}
                  {["partner","admin"].includes(user?.role) && t.approval_status === "approved" && t.user_id !== user.id && <button onClick={()=>openEdit(t)} className="text-blue-500 hover:text-blue-700" title="Edit (Partner override)" data-testid={`ts-edit-approved-${t.id}`}><Pencil className="h-4 w-4 inline"/></button>}
                  {t.user_id === user.id && ["draft","rejected"].includes(t.approval_status) && <button onClick={()=>submitDraft(t.id)} className="text-slate-400 hover:text-emerald-700" title="Submit"><Send className="h-4 w-4 inline"/></button>}
                  {t.approval_status !== "approved" && t.user_id === user.id && <button onClick={()=>remove(t.id)} className="text-slate-400 hover:text-red-600" title="Delete"><Trash2 className="h-4 w-4 inline"/></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </TabsContent>

      <TabsContent value="summary" className="mt-4" data-testid="ts-tab-content-summary">
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
            <div className="eyebrow">Timesheet Summary</div>
            <div className="flex items-center gap-2">
              <Label className="eyebrow">Group by</Label>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="rounded-sm w-44" data-testid="ts-summary-groupby"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="project">Project</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="category">Task Category</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {summary && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                <tr>
                  <th className="text-left p-4">{groupBy === "user" ? "User" : groupBy === "project" ? "Project" : groupBy === "client" ? "Client" : "Category"}</th>
                  {groupBy === "project" && <th className="text-left p-4">Client</th>}
                  <th className="text-right p-4">Entries</th>
                  <th className="text-right p-4">Total Hours</th>
                  <th className="text-right p-4">Billable Hours</th>
                  <th className="text-right p-4">Non-billable</th>
                  <th className="text-left p-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="ts-summary-table">
                {summary.rows.length === 0 && <tr><td colSpan={groupBy === "project" ? 7 : 6} className="p-8 text-center text-slate-500">No entries for this period.</td></tr>}
                {summary.rows.map((row, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="p-4 font-medium">{row.name}</td>
                    {groupBy === "project" && <td className="p-4 text-slate-600">{row.extra}</td>}
                    <td className="p-4 text-right serif-num">{row.entries}</td>
                    <td className="p-4 text-right serif-num font-semibold">{row.hours.toFixed(2)} h</td>
                    <td className="p-4 text-right serif-num text-emerald-700">{row.billable_hours.toFixed(2)} h</td>
                    <td className="p-4 text-right serif-num text-slate-500">{(row.hours - row.billable_hours).toFixed(2)} h</td>
                    <td className="p-4 text-xs text-slate-500"><span className="text-emerald-700">{row.approved} ✓</span> · <span className="text-blue-700">{row.submitted} ⌛</span>{row.rejected > 0 && <> · <span className="text-red-700">{row.rejected} ✗</span></>}</td>
                  </tr>
                ))}
                {summary.rows.length > 0 && (
                  <tr className="bg-slate-50 font-semibold border-t-2 border-slate-300" data-testid="ts-summary-totals">
                    <td colSpan={groupBy === "project" ? 2 : 1} className="p-4 uppercase tracking-[0.18em] text-xs text-slate-600">Total</td>
                    <td className="p-4 text-right serif-num">{summary.totals.entries}</td>
                    <td className="p-4 text-right serif-num">{summary.totals.hours.toFixed(2)} h</td>
                    <td className="p-4 text-right serif-num text-emerald-700">{summary.totals.billable_hours.toFixed(2)} h</td>
                    <td className="p-4 text-right serif-num text-slate-500">{(summary.totals.hours - summary.totals.billable_hours).toFixed(2)} h</td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </TabsContent>
      </Tabs>

      <Dialog open={compOffOpen} onOpenChange={setCompOffOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-sans text-2xl">Claim Comp-Off</DialogTitle></DialogHeader>
          <form onSubmit={submitCompOff} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="eyebrow">From</Label><Input type="date" required value={compOffForm.start_date} onChange={(e)=>setCompOffForm({...compOffForm, start_date: e.target.value})} className="mt-2 rounded-sm" data-testid="compoff-from"/></div>
              <div><Label className="eyebrow">To</Label><Input type="date" required value={compOffForm.end_date} onChange={(e)=>setCompOffForm({...compOffForm, end_date: e.target.value})} className="mt-2 rounded-sm" data-testid="compoff-to"/></div>
            </div>
            <div><Label className="eyebrow">Days</Label><Input type="number" step="0.5" min="0.5" required value={compOffForm.days} onChange={(e)=>setCompOffForm({...compOffForm, days: e.target.value})} className="mt-2 rounded-sm" data-testid="compoff-days"/></div>
            <div><Label className="eyebrow">Reason</Label><Textarea rows={3} required value={compOffForm.reason} onChange={(e)=>setCompOffForm({...compOffForm, reason: e.target.value})} className="mt-2 rounded-sm" data-testid="compoff-reason" placeholder="When/why did you work overtime?"/></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setCompOffOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="compoff-submit-btn">Submit Claim</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
